import { LOCAL_STORAGE, SESSION_STORAGE } from 'constants/app';
import { Loading } from 'carbon-components-react';
import PortalModal from 'components/PortalModal';
import { useStore } from 'effector-react';
import { useQueryParams } from 'hooks/useQueryParams';
import { FC, useLayoutEffect } from 'react';
import { APPLICATIONS, CATALOG, ENTITIES, USER_PROFILE } from 'routes/constants';
import { $userStore } from 'store/user';
import { authenticateUserFX, fetchUserProfileFX } from 'store/user/effects';
import { generatePath, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseJwt } from 'utils';
import { ProfileTypeEnum } from 'store/main/interface';
import { fetchPartnerOrganizationProfileFX } from '../../store/partnerOrganizations/effects';
import { fetchEntityProfileFX } from '../../store/entities/effects';
import { ExternalUserModalBehaviour } from '../../store/externalUserModal/interfaces';
import { openExternalUserModalEV } from '../../store/externalUserModal';

export const WithAuthWaiting: FC = () => {
  const user = useStore($userStore) as any;
  const [{ authSessionId, notaryToken, notaryTokenKey }] = useQueryParams();
  const history = useHistory();
  const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
  const token_key = localStorage.getItem(LOCAL_STORAGE.TOKEN_KEY);
  const language = localStorage.getItem(LOCAL_STORAGE.LANG);
  const userProfileLoading = useStore(fetchUserProfileFX.pending);
  const legalEntityProfileLoading = useStore(fetchEntityProfileFX.pending);
  const partnerOrganizationProfileLoading = useStore(fetchPartnerOrganizationProfileFX.pending);
  const authLoading = useStore(authenticateUserFX.pending);
  const hasTokens = (!!token && !!token_key) || (!!notaryToken && !!notaryTokenKey);
  const { i18n } = useTranslation();
  const shouldRequestUser = hasTokens && !user;
  const tokenInf = parseJwt(token || '');
  const POST_AUTH_REDIRECT_FINAL_RESTORE_PENDING = 'postAuthRedirectFinalRestorePending';
  const POST_AUTH_REDIRECT_RESTORED = 'postAuthRedirectRestored';

  const profileLoading =
    userProfileLoading || legalEntityProfileLoading || partnerOrganizationProfileLoading;
  const isLoading = authLoading || profileLoading;

  const redirectRestored = sessionStorage.getItem(POST_AUTH_REDIRECT_RESTORED) === 'true';

  const getRedirectUrl = (): string | null | undefined =>
    sessionStorage.getItem(SESSION_STORAGE.REDIRECT_URL) ||
    localStorage.getItem(SESSION_STORAGE.REDIRECT_URL);

  const getDefaultRedirectUrlByProfile = (): string => {
    const profileType = localStorage.getItem(LOCAL_STORAGE.PROFILE_TYPE) as ProfileTypeEnum;

    if (profileType === ProfileTypeEnum.PARTNER_ORGANIZATION) {
      return USER_PROFILE;
    }

    if (user?.accounts?.legal_entities_count > 0) {
      if (user.need_profile_submit || user.need_profile_review) {
        return USER_PROFILE;
      }

      return ENTITIES.LIST;
    }

    return CATALOG.LIST;
  };

  useLayoutEffect(() => {
    if (authSessionId) {
      if (isLoading || user || notaryToken) {
        return;
      }

      authenticateUserFX(authSessionId).then(() => {
        const redirectUrl = getRedirectUrl();

        if (redirectUrl) {
          sessionStorage.setItem(POST_AUTH_REDIRECT_FINAL_RESTORE_PENDING, 'true');
          sessionStorage.removeItem(POST_AUTH_REDIRECT_RESTORED);

          history.replace(redirectUrl);
        }
      });

      return;
    }

    if (!shouldRequestUser) {
      return;
    }

    if (notaryToken && notaryTokenKey) {
      localStorage.setItem(LOCAL_STORAGE.TOKEN, notaryToken);
      localStorage.setItem(LOCAL_STORAGE.TOKEN_KEY, notaryTokenKey);
      localStorage.setItem(LOCAL_STORAGE.LAST_TOKEN_REFRESH, new Date().toISOString());
    }

    // removed fetch of legal entity profile, because it was never called
    // changes were made in commit 34951424e7ac772f218edc1fe71656e1acff3a98
    // the previous logic read entity uuid after the call to the fetchUserProfileFX
    // in fetchUserProfileFX uuid was rewritten to natural persons uuid
    // so call to fetch legal entities profile was never made
    fetchUserProfileFX({}).then(async (response) => {
      const redirectUrl = getRedirectUrl() ?? getDefaultRedirectUrlByProfile();

      const isAuthLandingPage =
        window.location.pathname === '/' || window.location.pathname.includes('/login');

      if (redirectUrl && isAuthLandingPage) {
        sessionStorage.setItem(POST_AUTH_REDIRECT_RESTORED, 'true');

        history.replace(redirectUrl);
      }

      const externalUserModalBehaviour = localStorage.getItem(
        LOCAL_STORAGE.EXTERNAL_USER_MODAL_BEHAVIOUR
      );

      if (externalUserModalBehaviour === ExternalUserModalBehaviour.REOPEN) {
        localStorage.removeItem(LOCAL_STORAGE.EXTERNAL_USER_MODAL_BEHAVIOUR);

        const partnerOrganizations = response?.data?.accounts?.partner_organizations;

        const isEmpty =
          !partnerOrganizations ||
          (Array.isArray(partnerOrganizations)
            ? partnerOrganizations.length === 0
            : Object.keys(partnerOrganizations).length === 0);

        if (!isEmpty) {
          openExternalUserModalEV();
        }
      }
    });
  }, [authSessionId]);

  useLayoutEffect(() => {
    if (authSessionId) {
      return;
    }

    const isAuthLandingPage =
      window.location.pathname === '/' || window.location.pathname.includes('/login');

    if (!isAuthLandingPage) {
      return;
    }

    const redirectUrl = getRedirectUrl();

    if (redirectUrl && !redirectRestored) {
      const [redirectPathname, redirectSearch = ''] = redirectUrl.split('?');

      sessionStorage.setItem(POST_AUTH_REDIRECT_RESTORED, 'true');

      sessionStorage.removeItem(SESSION_STORAGE.REDIRECT_URL);
      localStorage.removeItem(SESSION_STORAGE.REDIRECT_URL);

      history.replace({
        pathname: redirectPathname,
        search: redirectSearch ? `?${redirectSearch}` : '',
        state: {
          redirectSearch: redirectSearch ? `?${redirectSearch}` : '',
        },
      });

      return;
    }

    if (!isLoading && user && !user.need_profile_submit && !tokenInf.legal_entity_uuid) {
      i18n.changeLanguage(user.ui_lang || language || 'lv');

      if (user.mode === 'internal') {
        history.push(CATALOG.LIST);

        return;
      }

      const submissions = window.location.pathname.includes('/submissions');
      const payment_completed = window.location.pathname.includes('/payment-completed');
      const submit_form = window.location.pathname.includes('/submit-form');
      const application_submit_uuid = localStorage.getItem('application_submit_uuid');
      const signing_document = window.location.pathname.includes('signing-document');
      const show_shared_payment = window.location.pathname.includes('/show_shared_payment');
      const create_basket = window.location.pathname.includes('/basket/create');
      const applications = window.location.pathname.includes('/applications');

      if (
        !submissions &&
        !payment_completed &&
        !submit_form &&
        !signing_document &&
        !show_shared_payment &&
        !create_basket &&
        !applications
      ) {
        if (application_submit_uuid) {
          history.replace(
            generatePath(APPLICATIONS.SUBMIT_FORM, { uuid: application_submit_uuid })
          );
          localStorage.removeItem('application_submit_uuid');

          return;
        }

        history.replace(getDefaultRedirectUrlByProfile());
      }
    }
  }, [profileLoading, isLoading, authSessionId]);

  if (isLoading) {
    return (
      <PortalModal>
        <Loading active />
      </PortalModal>
    );
  }

  return null;
};
